/**
 * Read-only ingredients+preparation rendering for one of an ingredient's
 * elaboration subgraphs (IngredientRecipeSubgraphResult, from
 * /api/ingredient/{id}/recipe-subgraphs) — the explorer's equivalent of
 * the recipe page's Elaboració view (RecipeTextRenderer.ts), reused for
 * the exact same visual sections (an ingredients list, a numbered
 * preparation) but working directly off a standalone subgraph's plain
 * node map instead of a RecipeStore: there is no live recipe/store here,
 * just a snapshot of one possible preparation the ingredient-recipe-
 * subgraphs search returned. Deliberately not reusing
 * RecipeTextRenderer's buildIngredientsHtml/buildPreparationHtml, which
 * are wired tightly to RecipeStore (hidden-node filtering, live node pool
 * lookups, hover wiring) — only the small pure formatting helpers are
 * shared, via textFormatting.ts, so quantity/preposition/thumbnail
 * rendering can't drift between the two.
 */
import { IngredientRecipeSubgraphNode, IngredientRecipeSubgraphResult, Quantity, TransformationInfo } from '../api/types.js';
import { evaluateTemplate, TemplateContext, TemplateNode } from '../template-engine.js';
import { unitCache } from '../unit-cache.js';
import { escapeHtml } from '../ui/escapeHtml.js';
import { resolveNodeTitle } from '../recipe_renderer/DisplayGraph.js';
import {
  capitalizeFirst, capitalizeFirstVisible, durationBadgeHtml, formatNumber, ingredientThumbnailHtml,
  mergeQuantities, preposition,
} from '../recipe_renderer/textFormatting.js';

type NodeMap = Record<string, IngredientRecipeSubgraphNode>;

/**
 * Build the "which recipe this comes from" attribution for an elaboration
 * view — deliberately unobtrusive (no thumbnail, muted text): a footnote
 * below the actual content, not a header competing with it for attention.
 * Callers place this after their ingredients/preparation content — see
 * ExplorerPage.ts and IngredientRecipeSelector.ts, which both frame it
 * identically.
 */
export function buildSubgraphFooterHtml(result: IngredientRecipeSubgraphResult): string {
  return (
    `<a href="/recipe/${escapeHtml(result.recipeId)}" class="text-xs text-[#888] hover:text-[#a0a0a0] transition-colors">` +
    `Extret de <span class="font-mono">${escapeHtml(result.recipeTitle)}</span>` +
    `</a>`
  );
}

/**
 * Build the ingredients-list HTML for a subgraph: leaf nodes (no
 * children) that carry an ingredient, grouped by ingredient id with
 * quantities merged per unit — same shape as the recipe page's own
 * ingredients list.
 */
export function buildSubgraphIngredientsHtml(nodes: NodeMap): string {
  const ingredientNodes = Object.values(nodes).filter(
    n => n.children.length === 0 && n.ingredientInfo?.ingredient
  );

  const groups = new Map<string, IngredientRecipeSubgraphNode[]>();
  for (const node of ingredientNodes) {
    const key = node.ingredientInfo!.ingredient!.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(node);
  }

  const seenKeys = new Set<string>();
  let html = '';

  for (const node of ingredientNodes) {
    const key = node.ingredientInfo!.ingredient!.id;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    const groupNodes = groups.get(key)!;
    const ingredient = groupNodes[0].ingredientInfo!.ingredient!;

    const quantities = groupNodes
      .map(n => n.ingredientInfo?.quantity)
      .filter((q): q is Quantity => q != null);
    const merged = mergeQuantities(quantities);

    const qtyStr = merged.length > 0
      ? merged.map(q => `${formatNumber(q.amount)} ${unitCache.getDisplayName(q.unit, q.amount)}`).join(' + ')
      : '';

    html += '<div class="py-1">';
    html += '  <div class="flex items-start gap-2">';
    html += ingredientThumbnailHtml(ingredient.images);
    html += '    <span>';
    if (qtyStr) {
      const prep = preposition(ingredient.name);
      html += `<span class="text-[#e0e0e0] font-mono text-sm">${qtyStr} ${prep}</span>`;
      html += `<span class="text-[#e0e0e0] font-mono text-sm">${escapeHtml(ingredient.name)}</span>`;
    } else {
      html += `      <span class="text-[#e0e0e0] font-mono text-sm">${escapeHtml(ingredient.name)}</span>`;
    }
    html += '    </span>';
    html += '  </div>';

    const primary = groupNodes[0];
    const commentLines: string[] = [];
    if (primary.description) commentLines.push(primary.description);
    if (primary.ingredientInfo?.description) commentLines.push(primary.ingredientInfo.description);
    for (const line of commentLines) {
      html += `  <div class="text-xs text-[#888] font-mono ml-6 mt-1">// ${escapeHtml(line)}</div>`;
    }
    html += '</div>';
  }

  if (!html) {
    html = '<div class="text-sm text-[#888] font-mono">(no hi ha ingredients)</div>';
  }
  return html;
}

/**
 * Build the numbered preparation-steps HTML for a subgraph: non-leaf
 * nodes (those with children), topologically sorted bottom-to-top,
 * rendered via each transformation's render template (with the same
 * hardcoded fallback the recipe page uses).
 */
export function buildSubgraphPreparationHtml(nodes: NodeMap): string {
  const stepNodes = Object.values(nodes).filter(n => n.children.length > 0);
  if (stepNodes.length === 0) {
    return '<div class="text-sm text-[#888] font-mono">(sense passos de preparació)</div>';
  }

  const parentsIndex = buildParentIndex(nodes);
  const sorted = topologicalSortBottomToTop(nodes, stepNodes);

  let html = '';
  let stepNumber = 0;
  sorted.forEach((node) => {
    // Each transformation renders as its own consecutive step row — see
    // RecipeTextRenderer.ts's renderStepTextsForNode() doc comment (same
    // rule, kept independent per this file's own header comment).
    const stepTexts = renderStepTextsForNode(nodes, node, parentsIndex);
    const transformationInfos = node.transformationInfos ?? [];
    const rowCount = Math.max(stepTexts.length, 1);

    for (let i = 0; i < rowCount; i++) {
      stepNumber += 1;
      const stepText = stepTexts[i] ?? null;

      html += `<div class="border-b border-[var(--accent-alpha-13)] py-2">`;
      html += `  <div class="flex items-baseline gap-2">`;
      html += `    <span class="text-xs text-[var(--accent)] font-bold font-mono flex-shrink-0">${stepNumber}.</span>`;
      if (stepText) {
        html += `    <span class="flex-1 text-sm text-[#e0e0e0] font-mono leading-relaxed">${stepText}</span>`;
      } else {
        html += `    <span class="flex-1 text-sm text-[#888] font-mono">(pas sense descripció)</span>`;
      }
      // Duration ("durada") — a right-aligned clock badge next to the
      // step's own text, same as RecipeTextRenderer.ts's buildPreparationHtml().
      html += durationBadgeHtml(transformationInfos[i]?.duration);
      html += `  </div>`;

      const commentLines: string[] = [];
      if (i === 0 && node.description) commentLines.push(node.description);
      if (transformationInfos[i]?.description) commentLines.push(transformationInfos[i].description!);
      if (i === 0) {
        for (const toolInfo of node.toolInfos ?? []) {
          if (toolInfo.description) commentLines.push(toolInfo.description);
        }
      }
      for (const line of commentLines) {
        html += `  <div class="text-xs text-[#888] font-mono ml-6 mt-1">// ${escapeHtml(line)}</div>`;
      }
      html += '</div>';
    }
  });

  return html;
}

// ============================================================================
// Internals
// ============================================================================

/** child id -> parent ids, for TemplateNode.parents (some templates reference them). */
function buildParentIndex(nodes: NodeMap): Map<string, string[]> {
  const parents = new Map<string, string[]>();
  for (const node of Object.values(nodes)) {
    for (const childId of node.children) {
      if (!parents.has(childId)) parents.set(childId, []);
      parents.get(childId)!.push(node.id);
    }
  }
  return parents;
}

/**
 * Longest distance to a leaf, memoized — used only to order sibling steps
 * that become ready at the same point in the topological sort, mirroring
 * DisplayNode.leafDistance's role in RecipeTextRenderer's own version.
 */
function computeLeafDistances(nodes: NodeMap): Map<string, number> {
  const dist = new Map<string, number>();
  const inProgress = new Set<string>();

  function compute(id: string): number {
    if (dist.has(id)) return dist.get(id)!;
    const node = nodes[id];
    if (!node || node.children.length === 0) {
      dist.set(id, 0);
      return 0;
    }
    if (inProgress.has(id)) return 0; // cycle guard — subgraphs shouldn't have one
    inProgress.add(id);
    const maxChildDist = Math.max(0, ...node.children.map(cid => compute(cid)));
    const d = maxChildDist + 1;
    dist.set(id, d);
    inProgress.delete(id);
    return d;
  }

  for (const id of Object.keys(nodes)) compute(id);
  return dist;
}

/**
 * Ranks candidate elaboration subgraphs for the ingredient-elaboration
 * picker — unrelated to a live recipe's own (now explicit, draggable)
 * step order, so still a depth-first, deepest-branch-first traversal
 * using each candidate's own `stepOrder` as a subtree-wide override
 * (unset counts as 0, positive pulls a branch toward the beginning,
 * negative pushes it toward the end, and whichever value — farthest from
 * 0 — is found anywhere within a branch becomes that whole branch's
 * effective value), same shape RecipeTextRenderer.ts's own sort used to
 * have before it became a plain stepOrder sort there.
 */
function topologicalSortBottomToTop(
  nodes: NodeMap,
  stepNodes: IngredientRecipeSubgraphNode[],
): IngredientRecipeSubgraphNode[] {
  const nodeSet = new Set(stepNodes.map(n => n.id));
  const nodeMap = new Map(stepNodes.map(n => [n.id, n]));
  const leafDistances = computeLeafDistances(nodes);

  const childrenOf = (id: string): string[] =>
    (nodeMap.get(id)?.children ?? []).filter(cid => nodeSet.has(cid));

  const subtreePriorityMemo = new Map<string, number>();
  const subtreePriority = (id: string): number => {
    const memoized = subtreePriorityMemo.get(id);
    if (memoized !== undefined) return memoized;
    subtreePriorityMemo.set(id, 0); // cycle guard, shouldn't be reachable in a DAG
    let best = nodeMap.get(id)?.stepOrder ?? 0;
    for (const childId of childrenOf(id)) {
      const childBest = subtreePriority(childId);
      if (Math.abs(childBest) > Math.abs(best)) best = childBest;
    }
    subtreePriorityMemo.set(id, best);
    return best;
  };

  // Stable sort — ties (equal priority, equal depth) keep `ids`'s own
  // order, which for a node's children is the order they were added in
  // the editor, and for the top-level branches below is `stepNodes`'s order.
  // Priority sorts descending (higher = earlier); depth still breaks ties
  // deepest-first.
  const orderSiblings = (ids: string[]): string[] =>
    [...ids].sort((a, b) => {
      const pa = subtreePriority(a);
      const pb = subtreePriority(b);
      if (pa !== pb) return pb - pa;
      return (leafDistances.get(b) ?? 0) - (leafDistances.get(a) ?? 0);
    });

  const result: IngredientRecipeSubgraphNode[] = [];
  const visited = new Set<string>();
  const visit = (id: string) => {
    if (visited.has(id)) return; // multi-parent node already emitted via an earlier branch
    visited.add(id);
    for (const childId of orderSiblings(childrenOf(id))) {
      visit(childId);
    }
    const node = nodeMap.get(id);
    if (node) result.push(node);
  };

  const hasParent = new Set<string>();
  for (const node of stepNodes) {
    for (const childId of childrenOf(node.id)) hasParent.add(childId);
  }
  const topIds = stepNodes.map(n => n.id).filter(id => !hasParent.has(id));

  for (const id of orderSiblings(topIds)) {
    visit(id);
  }

  return result;
}

/**
 * Render step text for every transformation usage on a node, in order —
 * see RecipeTextRenderer.ts's renderStepTextsForNode() doc comment (same
 * rule, kept independent per this file's own header comment).
 */
function renderStepTextsForNode(
  nodes: NodeMap,
  node: IngredientRecipeSubgraphNode,
  parentsIndex: Map<string, string[]>,
): string[] {
  const children = node.children.map(cid => nodes[cid]).filter((cn): cn is IngredientRecipeSubgraphNode => !!cn);
  if (children.length === 0) return [];

  return (node.transformationInfos ?? [])
    .map(trafoInfo => renderStepTextForUsage(nodes, node, children, trafoInfo, parentsIndex))
    .filter((text): text is string => text !== null);
}

function renderStepTextForUsage(
  nodes: NodeMap,
  node: IngredientRecipeSubgraphNode,
  children: IngredientRecipeSubgraphNode[],
  trafoInfo: TransformationInfo,
  parentsIndex: Map<string, string[]>,
): string | null {
  if (!trafoInfo.transformation) return null;

  const template = trafoInfo.transformation.renderTemplate;
  if (template) {
    const context = buildTemplateContext(nodes, node, trafoInfo, parentsIndex);
    const result = evaluateTemplate(template, context);
    const text = result || buildStepTextHardcoded(trafoInfo, children);
    return capitalizeFirstVisible(text);
  }

  const text = buildStepTextHardcoded(trafoInfo, children);
  return text ? capitalizeFirst(text) : null;
}

/**
 * Same rule as RecipeTextRenderer.ts's isBareToolNode() — see that file's
 * doc comment. Kept independent since this module deliberately doesn't
 * share code with RecipeTextRenderer (see this file's header comment).
 */
function hasTransformation(node: IngredientRecipeSubgraphNode): boolean {
  return !!node.transformationInfos?.some(ti => ti.transformation);
}

function isBareToolNode(node: IngredientRecipeSubgraphNode): boolean {
  return !!node.toolInfos?.length
    && !node.ingredientInfo?.ingredient
    && !hasTransformation(node);
}

function resolveKind(node: IngredientRecipeSubgraphNode): TemplateNode['kind'] {
  if (isBareToolNode(node)) return 'tool';
  if (node.ingredientInfo?.ingredient) return 'ingredient';
  if (hasTransformation(node)) return 'transformation';
  return undefined;
}

function buildTemplateContext(
  nodes: NodeMap,
  node: IngredientRecipeSubgraphNode,
  trafoInfo: TransformationInfo,
  parentsIndex: Map<string, string[]>,
): TemplateContext {
  return {
    self: nodeToTemplateNode(nodes, node, parentsIndex, new Set()),
    params: trafoInfo.parameters ?? {},
    duration: trafoInfo.duration ?? null,
    graph: {
      node: (id: string) => {
        const n = nodes[id];
        return n ? nodeToTemplateNode(nodes, n, parentsIndex, new Set()) : null;
      },
    },
  };
}

function nodeToTemplateNode(
  nodes: NodeMap,
  node: IngredientRecipeSubgraphNode,
  parentsIndex: Map<string, string[]>,
  visited: Set<string>,
): TemplateNode {
  if (visited.has(node.id)) {
    return {
      id: node.id, name: resolveNodeTitle(node), description: null, children: [], parents: [],
      kind: resolveKind(node),
    };
  }
  visited.add(node.id);

  const result: TemplateNode = {
    id: node.id,
    name: resolveNodeTitle(node),
    description: node.description ?? null,
    children: [],
    parents: [],
    quantityPrefix: getQuantityPrefix(node),
    kind: resolveKind(node),
  };

  result.children = node.children
    .map(cid => nodes[cid])
    .filter((cn): cn is IngredientRecipeSubgraphNode => !!cn)
    .map(c => nodeToTemplateNode(nodes, c, parentsIndex, visited));

  result.parents = (parentsIndex.get(node.id) ?? [])
    .map(pid => nodes[pid])
    .filter((pn): pn is IngredientRecipeSubgraphNode => !!pn)
    .map(p => nodeToTemplateNode(nodes, p, parentsIndex, visited));

  return result;
}

function buildStepTextHardcoded(trafoInfo: TransformationInfo, children: IngredientRecipeSubgraphNode[]): string {
  const trafoName = trafoInfo.transformation?.name;
  const trafoLabel = trafoName ? capitalizeFirst(trafoName) : '';
  const toolChildren = children.filter(isBareToolNode);
  const restChildren = children.filter(c => !isBareToolNode(c));
  const mainText = formatChildren(restChildren);
  const toolText = toolChildren.length
    ? ` en ${toolChildren.map(c => linkIngredient(resolveNodeTitle(c))).join(' i ')}`
    : '';
  return `${trafoLabel} ${mainText}${toolText}`.trim();
}

function formatChildren(children: IngredientRecipeSubgraphNode[]): string {
  if (children.length === 0) return '';
  const parts = children.map(c => formatChild(c));
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} i ${parts[1]}`;
  const last = parts[parts.length - 1];
  const rest = parts.slice(0, -1).join(', ');
  return `${rest} i ${last}`;
}

function formatChild(node: IngredientRecipeSubgraphNode): string {
  const qtyPrefix = getQuantityPrefix(node);
  const ingredientName = node.ingredientInfo?.ingredient?.name;

  if (qtyPrefix && ingredientName) return `${qtyPrefix}${linkIngredient(ingredientName)}`;
  if (ingredientName) return linkIngredient(ingredientName);
  // No ingredient of its own (an intermediate step's output, e.g. one
  // half of a "separa" split) — resolveNodeTitle falls back through the
  // node's own name, then its transformation, same as the recipe page's
  // own node titles, so this never surfaces the node's raw id.
  return linkIngredient(resolveNodeTitle(node));
}

function getQuantityPrefix(node: IngredientRecipeSubgraphNode): string | undefined {
  const qty = node.ingredientInfo?.quantity;
  const ingredientName = node.ingredientInfo?.ingredient?.name;
  if (qty && ingredientName) {
    const unitStr = unitCache.getDisplayName(qty.unit, qty.amount);
    return `${formatNumber(qty.amount)} ${unitStr} ${preposition(ingredientName)}`;
  }
  return undefined;
}

/**
 * Unlike RecipeTextRenderer's linkIngredient (which wires up hover/click
 * against a live node pool via data-node-id + CSS classes GraphVisualizer
 * listens for), there's no graph/store here to react to — just a visual
 * accent on ingredient mentions within step text.
 */
function linkIngredient(name: string): string {
  return `<span class="text-[var(--accent)]">${escapeHtml(name)}</span>`;
}
